import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  useGetExpensesQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
} from "../../features/api/expenseApiSlice";
import { useToast } from "../toast/ToastContext";
import AdminHeader from "../layout/AdminHeader";
import {
  FaPlus,
  FaTrash,
  FaPen,
  FaTimes,
  FaDollarSign,
  FaGasPump,
  FaBed,
  FaUtensils,
  FaTools,
  FaWrench,
  FaTag,
  FaCalendarAlt,
  FaCalendarCheck,
} from "react-icons/fa";
import "./adminExpenses.scss";
import ConfirmModal from "../modal/ConfirmModal";
import FuelLoading from "../../pages/FuelLoading";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function AdminExpenses() {
  const { showToast } = useToast();

  // Operational Mode Flags
  const [editingId, setEditingId] = useState(null);

  // 🟢 NEW States: Filter dashboard views by specific months/years
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // Defaults to current month
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Defaults to current year
  const [activeReceiptUrl, setActiveReceiptUrl] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);

  // Custom dropdown selector UI toggles
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Form Field Local State Anchors
  const [expenseTitle, setExpenseTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Fuel / Gas");
  const [associatedTrip, setAssociatedTrip] = useState("");
  const [transactionDate, setTransactionDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [paymentMethod, setPaymentMethod] = useState("Channel Credit Card");
  const [notes, setNotes] = useState("");

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    targetId: null,
    targetTitle: "",
  });

  // Data Fetching & Server Mutations
  const { data: expenses = [], isLoading, isError } = useGetExpensesQuery();
  const [createExpense, { isLoading: isCreating }] = useCreateExpenseMutation();
  const [updateExpense, { isLoading: isUpdating }] = useUpdateExpenseMutation();
  const [deleteExpenseLog, { isLoading: isDeleting }] =
    useDeleteExpenseMutation();
  console.log("EXPENSES", expenses);
  const isMutating = isCreating || isUpdating || isDeleting;

  // Handle outside click dropdown close listeners
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsMonthOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Update your reset workspace script to wipe the file selector state
  const resetFormWorkspace = () => {
    setEditingId(null);
    setExpenseTitle("");
    setAmount("");
    setCategory("Fuel / Gas");
    setAssociatedTrip("");
    setTransactionDate(new Date().toISOString().split("T")[0]);
    setPaymentMethod("Channel Credit Card");
    setNotes("");
    setReceiptFile(null); // 🟢 Clear file buffer
    if (document.getElementById("receipt-uploader")) {
      document.getElementById("receipt-uploader").value = ""; // Clear HTML element
    }
  };

  const handleEditInit = (item) => {
    setEditingId(item._id);
    setExpenseTitle(item.expenseTitle || "");
    setAmount((item.amount || 0) / 100);
    setCategory(item.category || "Fuel / Gas");
    setAssociatedTrip(item.associatedTrip || "");
    setTransactionDate(
      item.transactionDate
        ? item.transactionDate.split("T")[0]
        : new Date().toISOString().split("T")[0]
    );
    setPaymentMethod(item.paymentMethod || "Channel Credit Card");
    setNotes(item.notes || "");
  };

  // 🟢 GENERATE DYNAMIC YEAR DROPDOWN LIST FROM RECORDS CACHE
  const uniqueYearsList = useMemo(() => {
    const years = new Set([new Date().getFullYear()]);
    expenses.forEach((ex) => {
      if (ex.transactionDate)
        years.add(new Date(ex.transactionDate).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [expenses]);

  // 📊 CALCULATIONS RECKONER ENGINE: Dynamically isolate target monthly metrics blocks
  const filteredMonthlyMetrics = useMemo(() => {
    let monthlyTotalCents = 0;
    const monthlyCategoryTotals = {};

    // Filter list items on the fly matching user calendar selections
    const displayList = expenses.filter((ex) => {
      if (!ex.transactionDate) return false;
      const txDate = new Date(ex.transactionDate);
      return (
        txDate.getMonth() === selectedMonth &&
        txDate.getFullYear() === selectedYear
      );
    });

    displayList.forEach((ex) => {
      monthlyTotalCents += ex.amount || 0;
      monthlyCategoryTotals[ex.category] =
        (monthlyCategoryTotals[ex.category] || 0) + (ex.amount || 0);
    });

    return {
      displayList,
      monthlyTotalDollars: (monthlyTotalCents / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
      monthlyCategoryTotals,
    };
  }, [expenses, selectedMonth, selectedYear]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!expenseTitle.trim() || !amount || !transactionDate) {
      showToast(
        "Title, Amount, and Date are strictly required fields.",
        "error"
      );
      return;
    }

    // 🟢 BINARY MULTIPART GATEWAY: Compile parameters inside FormData instead of a standard JSON object
    const formData = new FormData();
    formData.append("expenseTitle", expenseTitle.trim());
    formData.append("amount", Number(amount));
    formData.append("category", category);
    formData.append(
      "associatedTrip",
      associatedTrip.trim() || "General Channel Overhead"
    );
    formData.append("transactionDate", transactionDate);
    formData.append("paymentMethod", paymentMethod);
    formData.append("notes", notes.trim());

    if (receiptFile) {
      formData.append("receiptImage", receiptFile); // 🟢 Attaches raw binary image packet
    }

    try {
      if (editingId) {
        // For updates, pass the ID via properties parameters matching your API slice bounds
        await updateExpense({ id: editingId, formData }).unwrap();
        showToast(`💸 Overwrite saved for "${expenseTitle}"!`, "success");
      } else {
        await createExpense(formData).unwrap();
        showToast(
          `💸 Expense for "${expenseTitle}" logged with receipt!`,
          "success"
        );
      }
      resetFormWorkspace();
    } catch (err) {
      showToast(
        "Failed to write expenditure data down pipeline channels.",
        "error"
      );
    }
  };

  const handleDeleteTrigger = (id, title) => {
    setModalConfig({ isOpen: true, targetId: id, targetTitle: title });
  };

  const executePurgeExpense = async () => {
    const { targetId, targetTitle } = modalConfig;
    setModalConfig({ isOpen: false, targetId: null, targetTitle: "" });
    try {
      await deleteExpenseLog(targetId).unwrap();
      showToast(`"${targetTitle}" purged from expense indexes.`, "success");
      if (editingId === targetId) resetFormWorkspace();
    } catch (err) {
      showToast("Failed to wipe entry from database collections.", "error");
    }
  };

  const getCategoryIcon = (cat) => {
    switch (cat) {
      case "Fuel / Gas":
        return <FaGasPump style={{ color: "#f59e0b" }} />;
      case "Lodging / Hotel":
        return <FaBed style={{ color: "#3b82f6" }} />;
      case "Food / Dining":
        return <FaUtensils style={{ color: "#10b981" }} />;
      case "Gear / Equipment":
        return <FaTools style={{ color: "#a855f7" }} />;
      case "Vehicle Maintenance":
        return <FaWrench style={{ color: "#ef4444" }} />;
      default:
        return <FaTag style={{ color: "#9ca3af" }} />;
    }
  };

  if (isLoading)
    return (
      <div className="admin-expense-fallback">
        <FuelLoading text="Opening ledger balances..." />
      </div>
    );
  if (isError)
    return (
      <div className="admin-expense-fallback">
        ⚠️ Error syncing operational cost telemetry registries.
      </div>
    );

  return (
    <div className="admin-expenses-manager fuel-fade-in">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title="Purge Expense Record"
        message={`Are you completely certain you want to wipe the expense item "${modalConfig.targetTitle}" from accounting entries?`}
        onConfirm={executePurgeExpense}
        onCancel={() =>
          setModalConfig({ isOpen: false, targetId: null, targetTitle: "" })
        }
      />

      {isMutating && (
        <FuelLoading text="Synchronizing cash outflows indexes..." fullScreen />
      )}

      <AdminHeader
        title="Channel Expense Control Deck"
        description="Monitor travel operations expenditures, log hotel/fuel logistics costs, and audit net profit metrics."
        badgeText="Accounting Desk"
      />

      {/* 🟢 DYNAMIC MONTH-BY-MONTH CALENDAR OVERVIEW SELECTOR ROW BAR */}
      <div className="month-selector-toolbar-row">
        <div className="toolbar-left-meta">
          <FaCalendarCheck className="calendar-indicator-icon" />
          <h3>Target Auditing Period Summary</h3>
        </div>

        <div className="toolbar-selectors-cluster" ref={dropdownRef}>
          {/* Un-blue Reusable Month Selector Tray */}
          <div
            className="mini-miles-custom-select month-select"
            onClick={() => setIsMonthOpen(!isMonthOpen)}
          >
            <div
              className={`select-trigger-box ${isMonthOpen ? "active" : ""}`}
            >
              <span>🗓️ {MONTH_NAMES[selectedMonth]}</span>
              <span>▼</span>
            </div>
            {isMonthOpen && (
              <ul className="select-options-tray">
                {MONTH_NAMES.map((name, index) => (
                  <li
                    key={name}
                    className={selectedMonth === index ? "selected" : ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedMonth(index);
                      setIsMonthOpen(false);
                    }}
                  >
                    {name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Native Dark Year Selector Input */}
          <select
            className="dark-year-native-dropdown"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {uniqueYearsList.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 📊 ROW 2: MONTHLY LEDGER EXPENSES SUMMARY CARD SPLITS */}
      <div className="expenses-summary-shelf">
        <div className="summary-card master-card">
          <span>{MONTH_NAMES[selectedMonth]} Expenses Gross</span>
          <h3>{filteredMonthlyMetrics.monthlyTotalDollars}</h3>
        </div>
        <div className="categories-chips-scroll-bar">
          {Object.entries(filteredMonthlyMetrics.monthlyCategoryTotals)
            .length === 0 ? (
            <p className="no-outflows-label-text">
              No active operational expenses logged for this month track window.
            </p>
          ) : (
            Object.entries(filteredMonthlyMetrics.monthlyCategoryTotals).map(
              ([catName, cents]) => (
                <div key={catName} className="category-metric-chip">
                  {getCategoryIcon(catName)}
                  <div className="chip-info">
                    <strong>
                      {(cents / 100).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 0,
                      })}
                    </strong>
                    <span>{catName}</span>
                  </div>
                </div>
              )
            )
          )}
        </div>
      </div>

      <div className="expenses-columns-grid">
        {/* LEFT PANEL: INTERACTIVE FORM WORKSPACE */}
        <section className="composer-form-card">
          <h2>
            {editingId
              ? "✏️ Adjust Expense Details"
              : "Log Operational Expense"}
          </h2>
          <p>
            Chart receipts, track regional trip cost balances, and balance out
            your payment logs.
          </p>

          <form onSubmit={handleFormSubmit} className="expense-core-form">
            <div className="form-input-row halves">
              <div className="field-group">
                <label>Expense / Vendor Title *</label>
                <input
                  type="text"
                  value={expenseTitle}
                  onChange={(e) => setExpenseTitle(e.target.value)}
                  placeholder="e.g., Pilot Gas Station, Motel 6"
                  required
                />
              </div>
              <div className="field-group">
                <label>Amount Gross {<FaDollarSign />} USD *</label>
                <div className="amount-input-icon-wrapper">
                  <span className="dollar-prefix-icon">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-input-row halves">
              <div className="field-group">
                <label>Logistics Category *</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="Fuel / Gas">⛽ Fuel / Gas</option>
                  <option value="Lodging / Hotel">🏨 Lodging / Hotel</option>
                  <option value="Food / Dining">🍔 Food / Dining</option>
                  <option value="Gear / Equipment">⚙️ Gear / Equipment</option>
                  <option value="Vehicle Maintenance">
                    🔧 Vehicle Maintenance
                  </option>
                  <option value="Monthly Software / Subscriptions">
                    💻 Monthly Software / Subscriptions
                  </option>
                  <option value="Permits / Park Passes">
                    🗺️ Permits / Park Passes
                  </option>
                  <option value="Marketing / Giveaways">
                    🎁 Marketing / Giveaways
                  </option>
                  <option value="Other Operational Costs">
                    📦 Other Operational Costs
                  </option>
                </select>
              </div>
              <div className="field-group">
                <label>Associated Trip / Route Context</label>
                <input
                  type="text"
                  value={associatedTrip}
                  onChange={(e) => setAssociatedTrip(e.target.value)}
                  placeholder="e.g., Des Moines to Galena (or leave blank)"
                />
              </div>
            </div>

            <div className="form-input-row halves">
              <div className="field-group">
                <label>Transaction Timestamp Date *</label>
                <input
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  required
                />
              </div>
              <div className="field-group">
                <label>Payment Method Channel</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="Channel Credit Card">
                    💳 Channel Credit Card
                  </option>
                  <option value="Business Debit">🏦 Business Debit</option>
                  <option value="Cash">💵 Cash</option>
                  <option value="Personal Out of Pocket">
                    👤 Personal Out of Pocket
                  </option>
                </select>
              </div>
            </div>

            <div className="field-group">
              <label>Expenditure Statement Breakdown Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Log extra details like gallons pumped, room numbers, or invoice confirmation codes..."
              />
            </div>

            {/* ==============================================================
                🟢 NEW: HARD-ANCHORED MULTIPART RECEIPT FILE UPLOADER INFRASTRUCTURE
               ============================================================== */}
            <div className="field-group receipt-upload-field-box">
              <label htmlFor="receipt-uploader">
                Attach Digital Receipt Document
              </label>
              <div className="receipt-uploader-row-chassis">
                <input
                  type="file"
                  id="receipt-uploader"
                  accept="image/*,application/pdf"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                  disabled={isMutating}
                />
                {receiptFile && (
                  <span className="file-ready-indicator">✓ File Staged</span>
                )}
              </div>

              {/* If editing an old expense that already has a receipt, show a link to view it */}
              {editingId &&
                expenses.find((ex) => ex._id === editingId)?.receiptUrl && (
                  <a
                    href={
                      expenses.find((ex) => ex._id === editingId).receiptUrl
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-current-receipt-link"
                  >
                    📄 View Currently Attached Receipt Cloud Asset
                  </a>
                )}
            </div>

            <div className="form-buttons-row-cluster">
              <button
                type="submit"
                className="fuel-primary-btn save-expense-btn"
                disabled={isMutating}
              >
                {editingId ? (
                  "Save Expense Overwrites"
                ) : (
                  <>
                    <FaPlus style={{ marginRight: "6px" }} /> Append Expense to
                    Ledger
                  </>
                )}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="cancel-editor-btn"
                  onClick={resetFormWorkspace}
                  disabled={isMutating}
                >
                  <FaTimes /> Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        {/* RIGHT PANEL: TRANSACTION HISTORY RUNNING MONTHLY STREAM FEED */}
        <section className="expenses-history-panel">
          <div className="panel-header-title">
            <h3>
              {MONTH_NAMES[selectedMonth]} Outflow Stream Logs (
              {filteredMonthlyMetrics.displayList.length})
            </h3>
          </div>
          <div className="admin-scrolling-expenses-wrapper">
            {filteredMonthlyMetrics.displayList.length === 0 ? (
              <p className="empty-ledger-text">
                No operational expenditures recorded inside database files
                tracks for this month.
              </p>
            ) : (
              <div className="expenses-stack-layout">
                {filteredMonthlyMetrics.displayList.map((item) => (
                  <div
                    key={item._id}
                    className={`expense-row-card ${
                      editingId === item._id ? "expense-row-card--editing" : ""
                    }`}
                  >
                    <div className="card-category-avatar">
                      {getCategoryIcon(item.category)}
                    </div>

                    <div className="expense-meta-block">
                      <div className="card-headline-line">
                        <h4>{item.expenseTitle}</h4>
                        <span className="card-price-tag">
                          -
                          {((item.amount || 0) / 100).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </span>
                      </div>

                      <div className="card-sub-attribution-row">
                        <span>
                          <FaCalendarAlt />{" "}
                          {new Date(item.transactionDate).toLocaleDateString()}
                        </span>
                        <span className="trip-badge">
                          📍 {item.associatedTrip || "General Overhead"}
                        </span>
                        <span className="method-badge">
                          {item.paymentMethod}
                        </span>

                        {/* 🟢 NEW: CONDITIONAL RECEIPT VIEW CHANNELS LINK TRIGGER BUTTON */}
                        {item.receiptUrl && (
                          <button
                            type="button"
                            className="inline-receipt-preview-trigger-btn"
                            onClick={() => setActiveReceiptUrl(item.receiptUrl)}
                          >
                            📄 View Receipt
                          </button>
                        )}
                      </div>

                      {item.notes && (
                        <p className="expense-notes-snippet">“{item.notes}”</p>
                      )}
                    </div>

                    <div className="expense-row-card__controls">
                      <button
                        type="button"
                        className="btn-row-action edit"
                        onClick={() => handleEditInit(item)}
                        disabled={isMutating}
                      >
                        <FaPen />
                      </button>
                      <button
                        type="button"
                        className="btn-row-action delete"
                        onClick={() =>
                          handleDeleteTrigger(item._id, item.expenseTitle)
                        }
                        disabled={isMutating}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ==============================================================
          🟢 NEW: PORTAL POPUP GLASS LIGHTBOX MODAL FOR RECEIPTS AUDITING
         ============================================================== */}
            {activeReceiptUrl && (
              <div
                className="receipt-lightbox-overlay-chassis fuel-fade-in"
                onClick={() => setActiveReceiptUrl(null)}
              >
                <div
                  className="lightbox-central-box-frame"
                  onClick={(e) => e.stopPropagation()}
                >
                  <header className="lightbox-header-bar">
                    <span>Auditing Digital Receipt Proof</span>
                    <button
                      type="button"
                      className="close-lightbox-cross-btn"
                      onClick={() => setActiveReceiptUrl(null)}
                    >
                      ✕
                    </button>
                  </header>

                  <div className="receipt-img-container-view">
                    <img
                      src={activeReceiptUrl}
                      alt="AWS S3 Financial Receipt Voucher"
                      loading="lazy"
                    />
                  </div>

                  <footer className="lightbox-action-footer-panel">
                    <a
                      href={activeReceiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fuel-secondary-btn-link"
                    >
                      Open Original File in New Tab 🌐
                    </a>
                  </footer>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
